/**
 * @typedef BackInStockAttributes
 *
 * @property {boolean} [accepts_marketing]
 * @property {string} [phone_number] Required if email is null
 * @property {number} [quantity_required]
 */

/**
 * @typedef {Object} BackInStockSuccessResponse
 *
 * @property {'OK'} status
 * @property {string} message
 */

/**
 * @typedef {Object} BackInStockErrorResponse
 *
 * @property {'Error'} status
 * @property {Record<string, string[]>} errors
 */

class BackInStock {
  /**
   * @note It might be worth abstracting this further, for example simplifying the function signature
   * to have object containing email and phone number. That way this API could be easier to swap out
   * for other back ends in the future. It doesn't seem necessary at the moment though. - DW
   *
   * @param {string | null} email
   * @param {number} variantId
   * @param {number} productId
   * @param {BackInStockAttributes} [attributes]
   */
  static async subscribe (email, variantId, productId) {
    /** @type {BackInStockSuccessResponse | BackInStockErrorResponse} */

    const bodyData = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: `${email}`,
              },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    }

    const options = {
      method: 'POST',
      headers: {
        revision: window.theme.klaviyo.apiVersion,
        'content-type': 'application/json',
      },
      body: JSON.stringify(bodyData),
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${window.theme.klaviyo.apiKey}`,
      options,
    )

    if (!response.ok) {
      const data = await response.json()
      throw data.errors
    }

    return response
  }

  // To-do: Run Ometria newsletter subscription
}

class BackInStockForm extends window.BAO.CustomElement() {
  static get observedAttributes () {
    return ['data-variant-title']
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (oldValue === null && newValue === '') return

    switch (name) {
      case 'data-variant-title':
        this.appendTitleToLabel()
        break
    }
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.handleFormSubmit.bind(this),
    )

    this.listeners.add(
      document,
      window.BAO.EVENTS.VARIANT.CHANGED,
      this.handleVariantChange.bind(this),
    )

    this.listeners.add(
      this.els.close.element,
      'click',
      this.handleClose.bind(this),
    )

    this.listeners.add(document, 'drawerOpen', this.handleDrawerOpen.bind(this))

    if (this.externalEls?.trigger) {
      this.externalEls.trigger.elements.forEach(trigger => {
        this.listeners.add(trigger, 'click', this.handleReveal.bind(this))
      })
    }
  }

  close () {
    this.setAttribute('aria-hidden', true)

    this.externalEls.productform.element.setAttribute('aria-hidden', false)
  }

  handleDrawerOpen (event) {
    const { detail } = event

    this.state = 'idle'

    if (detail.key === 'bis') {
      this.dataset.variantId = detail.variantId
      this.dataset.variantTitle = detail.variantTitle
    }
  }

  /** @param {MouseEvent} event */
  handleClose (event) {
    event.preventDefault()

    this.close()
  }

  /** @param {SubmitEvent} event */
  handleFormSubmit (event) {
    event.preventDefault()

    this.formData = new FormData(this.els.form.element)

    window.dataLayer.push({
      event: 'backInStockFormSubmitted',
      variantId: this.variantId,
      productId: this.productId,
      newsletterSizeChecked: this.els['newsletter-size'].element.checked,
      // newsletterChecked: this.els.newsletter.element.checked,
    })

    // // Additional data for Klaviyo
    // const klaviyoProductUrl = this.formData.get('klaviyo_product_url')
    // const klaviyoFeaturedImage = this.formData.get('klaviyo_featured_image')
    // const klaviyoFeaturedImageAlt = this.formData.get(
    //   'klaviyo_featured_image_alt',
    // )
    // const klaviyoLifestyleImage = this.formData.get('klaviyo_lifestyle_image')
    // const klaviyoLifestyleImageAlt = this.formData.get(
    //   'klaviyo_lifestyle_image_alt',
    // )
    // const klaviyoProductPrice = this.formData.get('klaviyo_product_price')
    // const klaviyoProductComparePrice = this.formData.get(
    //   'klaviyo_product_compare_price',
    // )
    // const klaviyoStoreCurrency = this.formData.get('klaviyo_store_currency')

    // const attributes = {
    //   klaviyo_product_url: klaviyoProductUrl,
    //   klaviyo_featured_image: klaviyoFeaturedImage,
    //   klaviyo_featured_image_alt: klaviyoFeaturedImageAlt,
    //   klaviyo_lifestyle_image: klaviyoLifestyleImage,
    //   klaviyo_lifestyle_image_alt: klaviyoLifestyleImageAlt,
    //   klaviyo_product_price: klaviyoProductPrice,
    //   klaviyo_product_compare_price: klaviyoProductComparePrice,
    //   klaviyo_store_currency: klaviyoStoreCurrency,
    // }

    // this.newsletterSubscribe()
    this.subscribe()
  }

  /** @param {CustomEvent} event */
  handleVariantChange (event) {
    const variant = event?.detail?.variant?.data
    if (!variant) return

    this.state = 'idle'

    // If variant is available, hide the form.
    if (variant.available) {
      this.setAttribute('aria-hidden', true)

      return
    }

    this.dataset.variantId = variant.id
  }

  /** @param {FormEvent} event */
  handleReveal (event) {
    // On the PDP, the trigger is the add to cart button.
    const trigger = event.target.closest('[data-product-add-to-cart]')

    // Do nothing else if the variant is available.
    // This will allow the add to cart event to fire.
    const isBisActive = trigger?.dataset?.bis === 'true'
    if (!isBisActive) return

    event.preventDefault()

    // Is event fired from inside the size guide?
    if (trigger.hasAttribute('data-size-guide-atc')) {
      window.dataLayer.push({
        event: 'backInStockFromSizeGuide',
        productId: this.productId,
      })

      // Close the size guide drawer.
      window.drawers.activeDrawerKey = ''
    }

    if (this.getAttribute('aria-hidden') === 'true') {
      this.externalEls.productform.element.setAttribute('aria-hidden', true)
      this.setAttribute('aria-hidden', false)
    } else {
      this.setAttribute('aria-hidden', true)
    }

    this.els.email.element.focus()
  }

  appendTitleToLabel () {
    // Append the variant title to the 'notify me about' label.
    if (this.els.notify?.exists) {
      this.els.notify.element.textContent = `${this.els.notify.element.dataset.text} '${this.dataset.variantTitle}'`
    }
  }

  async subscribe (test = false) {
    if (test || window.Shopify.shop === 'data-migration-dev.myshopify.com') {
      this.els['notify-thanks'].element.setAttribute('aria-hidden', false)
      this.showSuccess()
      return
    }

    const response = await BackInStock.subscribe(
      this.formData.get('email'),
      this.variantId,
      this.productId,
    )

    this.els['notify-thanks'].element.setAttribute('aria-hidden', false)

    this.showSuccess()

    this.log('subscribe', { response })
  }

  // Newsletter opt-in temporarily removed during the Klaviyo to Ometria migration.
  // Restore with the checkbox in snippets/product-back-in-stock.liquid once it posts to Ometria.
  // async newsletterSubscribe () {
  //   if (this.formData.get('newsletter') === 'subscribe') {
  //     const response = await BackInStock.newsletterSubscribe(
  //       this.formData.get('email'),
  //     )

  //     this.els['newsletter-thanks'].element.setAttribute('aria-hidden', false)

  //     this.showSuccess()

  //     this.log('newsletterSubscribe', { response })

  //     return response.ok
  //   }

  //   return false
  // }

  showSuccess () {
    this.state = 'success'

    setTimeout(() => {
      if (this.dataset.isDrawer === 'true') {
        window.drawers.activeDrawerKey = ''
      } else {
        this.state = 'idle'

        this.close()
      }
    }, 1000)
  }

  /** @returns {'idle' | 'success'} */
  get state () {
    const visibleState = this.els.state.elements.find(
      view => view.getAttribute('aria-hidden') !== 'true',
    )

    return visibleState.dataset.view
  }

  set state (state) {
    this.els.state.elements.forEach(element => {
      element.setAttribute('aria-hidden', element.dataset.state !== state)
    })
  }

  get productId () {
    return Number(this.dataset.productId)
  }

  get variantId () {
    return Number(this.dataset.variantId)
  }
}

if (!window.customElements.get('back-in-stock-form')) {
  window.customElements.define('back-in-stock-form', BackInStockForm, {
    extends: 'aside',
  })
}
